import { useState, useEffect } from 'react';
import { message } from 'antd';
import subService, { CreateCheckoutRequest, SubscriptionStatus, AddUsersRequest } from '../services/sub/sub.service';

export const useSubscription = (autoFetchStatus: boolean = true) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await subService.getSubsByCompanyId();
      setSubscriptionStatus(status as any);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Abonelik durumu alınırken hata oluştu';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const createCheckoutSession = async (data: CreateCheckoutRequest) => {
    setLoading(true);
    try {
      const result = await subService.createCheckoutSession(data);
      message.success('Ödeme sayfası oluşturuldu');
      // Backend dokümantasyonuna göre checkoutFormContent döndürülüyor
      return result
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Ödeme sayfası oluşturulurken hata oluştu';
      setError(errorMessage);
      message.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const retryPayment = async () => {
    setLoading(true);
    try {
      const result = await subService.retryPayment();
      message.success('Ödeme yeniden deneme işlemi başlatıldı');
      await fetchSubscriptionStatus(); // Durumu güncelle
      // Backend dokümantasyonuna göre checkoutFormContent döndürülüyor
      return {
        data: result.data?.checkoutFormContent || result.data
      };
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Ödeme yeniden denenirken hata oluştu';
      setError(errorMessage);
      message.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addUsers = async (data: AddUsersRequest) => {
    setLoading(true);
    try {
      const result = await subService.addUsers(data);
      message.success('Ödeme sayfası oluşturuldu');
      localStorage.setItem('checkoutsession', result.checkoutForm.token);
      localStorage.setItem('conversationId', result.checkoutForm.conversationId);

      // IYZICO guide'ına göre checkout form döndürüyor
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Kullanıcı ekleme ödemesi başlatılırken hata oluştu';
      setError(errorMessage);
      message.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async () => {
    setLoading(true);
    try {
      const result = await subService.cancelSubscription();
      message.success('Abonelik başarıyla iptal edildi');
      await fetchSubscriptionStatus(); // Durumu güncelle
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Abonelik iptal edilirken hata oluştu';
      setError(errorMessage);
      message.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trial':
        return 'processing';
      case 'past_due':
        return 'warning';
      case 'canceled':
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'trial':
        return 'Deneme';
      case 'past_due':
        return 'Ödeme Gecikti';
      case 'canceled':
        return 'İptal Edildi';
      case 'expired':
        return 'Süresi Doldu';
      default:
        return 'Bilinmiyor';
    }
  };

  useEffect(() => {
    if (autoFetchStatus) {
      fetchSubscriptionStatus();
    }
  }, [autoFetchStatus]);

  return {
    subscriptionStatus,
    loading,
    error,
    fetchSubscriptionStatus,
    createCheckoutSession,
    retryPayment,
    addUsers,
    cancelSubscription,
    getStatusColor,
    getStatusText
  };
}; 